import { partials } from '@shopify/partial-rendering';

// All URLs and customer-facing messages come from the owning Liquid block.
const propertiesKey = (properties = {}) => JSON.stringify(Object.entries(properties || {})
  .filter(([, value]) => value !== '' && value != null)
  .map(([key, value]) => [key, String(value)])
  .sort(([a], [b]) => a.localeCompare(b)));

const lineIdentity = (line) => JSON.stringify([
  String(line.variant_id ?? line.id),
  String(line.selling_plan_allocation?.selling_plan?.id ?? ''),
  propertiesKey(line.properties),
]);

const formIdentity = (data) => {
  const properties = {};
  for (const [name, value] of data.entries()) {
    const match = name.match(/^properties\[(.*)\]$/);
    if (match) properties[match[1]] = typeof value === 'string' ? value : `${value.name}:${value.size}:${value.lastModified}`;
  }
  return JSON.stringify([String(data.get('id') || ''), String(data.get('selling_plan') || ''), propertiesKey(properties)]);
};

export class PurchaseError extends Error {
  constructor(code, message = '') { super(message); this.code = code; }
}

// The session serializes writes and retains the server's actual line key and quantity.
export class RukiaPurchaseSession {
  constructor({ addUrl, changeUrl, cartUrl, request = (...args) => fetch(...args) }) {
    this.urls = { addUrl, changeUrl, cartUrl };
    this.request = request;
    this.selection = '';
    this.line = null;
    this.busy = false;
    this.uncertain = null;
    this.warning = '';
  }

  select(data) {
    const next = formIdentity(data);
    if (next !== this.selection) {
      this.selection = next;
      this.line = null;
    }
    return next;
  }

  async json(url, options = {}) {
    const response = await this.request(url, {
      credentials: 'same-origin', cache: 'no-store',
      ...options,
      headers: { Accept: 'application/json', ...options.headers },
    });
    let result;
    try { result = await response.json(); } catch { throw new PurchaseError('uncertain'); }
    if (!response.ok || result?.status >= 400) {
      const error = new PurchaseError(response.status < 500 ? 'rejected' : 'uncertain',
        typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : '');
      throw error;
    }
    return result;
  }

  async cart() {
    const cart = await this.json(this.urls.cartUrl);
    if (!Array.isArray(cart.items)) throw new PurchaseError('uncertain');
    return cart;
  }

  find(cart, previous = this.line) {
    if (!previous) return null;
    const exact = cart.items.find((line) => line.key === previous.key);
    if (exact) return exact;
    const matches = cart.items.filter((line) => lineIdentity(line) === lineIdentity(previous));
    return matches.length === 1 ? matches[0] : null;
  }

  accept(line, selection) {
    if (!line?.key || !(line.quantity > 0)) throw new PurchaseError('uncertain');
    if (this.selection === selection) this.line = line;
    return line;
  }

  async add(data) {
    if (this.busy) throw new PurchaseError('busy');
    const selection = this.select(data);
    if (this.line) return this.line;
    this.busy = true;
    this.warning = '';
    let before;
    let submitted = false;
    try {
      if (this.uncertain) {
        const recovered = await this.reconcileUncertain();
        if (!recovered) throw new PurchaseError('uncertain');
        if (this.line) return recovered;
      }
      before = await this.cart();
      submitted = true;
      const result = await this.json(this.urls.addUrl, { method: 'POST', body: data });
      return this.accept(result.items?.[0] ?? result, selection);
    } catch (error) {
      if (submitted) {
        const quantityBefore = before.items.filter((line) => lineIdentity(line) === selection)
          .reduce((sum, line) => sum + line.quantity, 0);
        this.uncertain = { selection, quantityBefore, requested: Number(data.get('quantity') || 1) };
        try {
          const recovered = await this.reconcileUncertain(error.code === 'rejected');
          if (recovered) {
            this.warning = error.code === 'rejected' ? error.message : '';
            return recovered;
          }
        } catch {
          // Keep the unresolved operation to prevent a duplicate POST.
        }
        if (error.code === 'rejected') this.uncertain = null;
      }
      throw error;
    } finally { this.busy = false; }
  }

  async reconcileUncertain(allowPartial = false) {
    if (!this.uncertain) return null;
    const { selection, quantityBefore, requested } = this.uncertain;
    const cart = await this.cart();
    const matches = cart.items.filter((line) => lineIdentity(line) === selection);
    const total = matches.reduce((sum, line) => sum + line.quantity, 0);
    // Never guess which line to mutate when an automatic discount split a variant.
    if (matches.length === 1 && (total >= quantityBefore + requested || (allowPartial && total > quantityBefore))) {
      this.uncertain = null;
      return this.accept(matches[0], selection);
    }
    return null;
  }

  async update(quantity) {
    if (this.busy) throw new PurchaseError('busy');
    if (!Number.isInteger(quantity) || quantity < 1) throw new PurchaseError('quantity');
    if (!this.line) throw new PurchaseError('missing');
    this.busy = true;
    const selection = this.selection;
    const previous = this.line;
    try {
      const current = this.find(await this.cart(), previous);
      if (!current) {
        if (selection === this.selection) this.line = null;
        throw new PurchaseError('missing');
      }
      if (current.quantity === quantity) return this.accept(current, selection);
      let cart;
      try {
        cart = await this.json(this.urls.changeUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: current.key, quantity }),
        });
      } catch (error) {
        // A dropped response can follow a committed write. Read back before retrying.
        const actual = this.find(await this.cart(), current);
        if (actual) this.accept(actual, selection);
        else if (selection === this.selection) this.line = null;
        if (actual?.quantity === quantity) return actual;
        throw error;
      }
      const updated = this.find(cart, current);
      if (!updated) {
        if (selection === this.selection) this.line = null;
        throw new PurchaseError('missing');
      }
      return this.accept(updated, selection);
    } finally { this.busy = false; }
  }

  async verify() {
    if (this.busy) throw new PurchaseError('busy');
    if (!this.line) throw new PurchaseError('missing');
    this.busy = true;
    const selection = this.selection;
    const previous = this.line;
    try {
      const actual = this.find(await this.cart(), previous);
      if (!actual) {
        if (selection === this.selection) this.line = null;
        throw new PurchaseError('missing');
      }
      return this.accept(actual, selection);
    } finally { this.busy = false; }
  }
}

class SRukiaPurchase extends HTMLElement {
  #session;
  #ac;
  #observer;
  #form;
  #pending = false;
  #changingQuantity = false;

  connectedCallback() {
    if (this.#ac) return;
    if (!this.dataset.addUrl || !this.dataset.changeUrl || !this.dataset.cartUrl) return;
    this.#session ??= new RukiaPurchaseSession(this.dataset);
    this.#ac = new AbortController();
    const options = { signal: this.#ac.signal };
    this.addEventListener('submit', (event) => this.#submit(event), options);
    this.addEventListener('change', (event) => this.#change(event), options);
    this.addEventListener('s-quantity:change', (event) => this.#change(event), options);
    // Variant partial replacement can change availability and quantity rules.
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'aria-busy'] });
    this.#sync();
    customElements.whenDefined('s-variant-picker').then(() => {
      if (!options.signal.aborted && this.isConnected) this.#sync();
    }).catch(() => {
      // Keep the native selector usable if the variant component cannot load.
    });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
    this.#observer?.disconnect();
    // Do not abort a cart POST: the server may already have committed it.
  }

  #sync() {
    this.#form = this.querySelector('form[data-rukia-add-form]');
    if (!this.#form) return;
    this.#session.select(new FormData(this.#form));
    const button = this.#button;
    const label = button?.querySelector('[data-rukia-purchase-label]');
    const picking = this.querySelector('s-variant-picker[aria-busy="true"]');
    const unavailable = button?.disabled || button?.dataset.available === 'false';
    const busy = this.#pending || Boolean(picking);
    const quantitySelector = this.querySelector('s-quantity-selector');
    if (quantitySelector) quantitySelector.inert = this.#pending;
    const variantPicker = this.querySelector('s-variant-picker');
    const nativeVariantForm = this.querySelector('[data-native-variant-form]');
    let variantReady = false;
    if (variantPicker) {
      const VariantPicker = customElements.get('s-variant-picker');
      if (typeof VariantPicker === 'function') {
        try {
          customElements.upgrade(variantPicker);
          variantReady = variantPicker instanceof VariantPicker;
        } catch {
          // A failed element upgrade leaves the server-rendered GET form visible.
        }
      }
      variantPicker.hidden = !variantReady;
      variantPicker.inert = this.#pending;
    }
    if (nativeVariantForm) nativeVariantForm.hidden = variantReady;
    const state = unavailable ? 'unavailable' : busy ? 'pending' : this.#session.line ? 'checkout' : 'add';
    if (this.dataset.purchaseState !== state) this.dataset.purchaseState = state;
    if (this.getAttribute('aria-busy') !== String(busy)) this.setAttribute('aria-busy', String(busy));
    if (button?.getAttribute('aria-disabled') !== String(busy || unavailable)) button?.setAttribute('aria-disabled', String(busy || unavailable));
    if (label && !unavailable) {
      const text = busy
        ? (this.#session.line ? this.dataset.labelUpdating : this.dataset.labelAdding)
        : this.#session.line ? this.dataset.labelCheckout : this.dataset.labelAdd;
      if (text && label.textContent !== text) label.textContent = text;
    }
  }

  get #button() { return this.#form?.querySelector('[data-rukia-purchase-button]'); }
  get #quantity() { return this.#form?.querySelector('[name="quantity"]'); }

  async #submit(event) {
    if (event.target !== this.#form) return;
    if (event.submitter && event.submitter !== this.#button) return;
    const checkout = this.querySelector('form[data-rukia-checkout-form]');
    // Preserve the native product submission if the enhancement is incomplete.
    if (!checkout || !this.#button) return;
    event.preventDefault();
    this.#sync();
    if (this.#pending || this.#session.busy || this.#button.disabled || this.#button.dataset.available === 'false'
      || this.querySelector('s-variant-picker[aria-busy="true"]')) return;
    if (!this.#form.reportValidity()) return;
    const data = new FormData(this.#form);
    const selection = this.#session.select(data);
    const hasLine = Boolean(this.#session.line);
    await this.#perform(async () => {
      if (!hasLine) {
        await this.#session.add(data);
        await this.#refresh();
        if (this.#session.warning) this.#error(this.#session.warning);
        if (this.#session.selection === selection) this.#status(this.dataset.addedMessage);
        return;
      }
      const desired = Number(data.get('quantity') || 1);
      if (desired !== this.#session.line.quantity) {
        await this.#session.update(desired);
        await this.#refresh();
      } else await this.#session.verify();
      if (!this.isConnected || this.#session.selection !== selection || !this.#session.line) return;
      const nativeSubmitter = checkout.querySelector('[name="checkout"]');
      if (nativeSubmitter?.matches('button, input[type="submit"]')) checkout.requestSubmit(nativeSubmitter);
      else checkout.requestSubmit();
    });
  }

  #change(event) {
    if (!this.#form || this.#changingQuantity) return;
    const quantityEvent = event.type === 's-quantity:change' || event.target === this.#quantity;
    this.#sync();
    if (!quantityEvent || !this.#session.line || this.#pending) return;
    if (!this.#quantity?.checkValidity()) { this.#error(this.dataset.quantityError); return; }
    const desired = Number(this.#quantity.value);
    if (desired === this.#session.line.quantity) return;
    this.#perform(async () => {
      await this.#session.update(desired);
      await this.#refresh();
      this.#status(this.dataset.updatedMessage);
    });
  }

  async #perform(action) {
    this.#pending = true;
    this.#error('');
    this.#sync();
    try { await action(); }
    catch (error) {
      const messages = {
        uncertain: this.dataset.uncertainMessage,
        missing: this.dataset.lineMissingMessage,
        quantity: this.dataset.quantityError,
      };
      this.#error(messages[error.code] || (error.code === 'rejected' && error.message) || this.dataset.errorMessage);
    } finally {
      this.#pending = false;
      if (this.#session.line && this.#quantity) {
        this.#changingQuantity = true;
        this.#quantity.value = String(this.#session.line.quantity);
        this.#quantity.dispatchEvent(new Event('change', { bubbles: true }));
        this.#changingQuantity = false;
      }
      this.#sync();
    }
  }

  async #refresh() {
    try {
      const cart = customElements.get('s-cart');
      if (cart?.refreshMountedCarts) await cart.refreshMountedCarts();
      else {
        const names = (this.dataset.partials || '').split(',').map((name) => name.trim()).filter(Boolean);
        if (names.length) partials.apply(await partials.fetch(...names));
      }
    } catch {
      // A display-refresh failure must never make a confirmed add appear unsuccessful.
      this.#error(this.dataset.refreshErrorMessage || this.dataset.errorMessage);
    }
  }

  #error(message) {
    const alert = this.querySelector('[data-add-to-cart-error]');
    if (!alert) return;
    alert.textContent = message || '';
    alert.hidden = !message;
  }

  #status(message) {
    const status = this.querySelector('[data-purchase-status]');
    if (status) status.textContent = message || '';
  }
}

if (!customElements.get('s-rukia-purchase')) customElements.define('s-rukia-purchase', SRukiaPurchase);
